import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  FundingStageName,
  useSaveCampaignMutation,
  useFundraisingCampaignModalQuery as useBaseQuery
} from 'generated/graphql';
import _ from 'lodash';
import {
  refetchQueries,
  useLoading,
  validationUtil
} from 'modules';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';



const __formSchema = yup.object({
  fundraiseName: validationUtil.nonEmptyStringSchema.required('Name is required.'),
  fundraiseDescription: yup.string().max(250, 'Must be no more than 250 characters'),
  targetCloseDate: yup.string(),
  targetAmountDollars: yup.number().positive('Number must be non-negative').required('Target Amount Dollars is required'),
  fundingStage: yup.mixed<FundingStageName>().required('Funding Stage is required.')
}).required();

export type IFormData = yup.InferType<typeof __formSchema>;



export function useFundraisingCampaignModal(campaignId?: string) {
  const tuple = useBaseQuery({
    variables: {
      id: campaignId ?? ''
    },

    skip: _.isUndefined(campaignId) || _.isEmpty(campaignId)
  });

  const [saveCampaignMutation, saveCampaignMutationTuple] = __useSaveCampaignMutation(campaignId);
  const loading = useLoading([tuple, saveCampaignMutationTuple]);

  const form = useForm<IFormData>({
    resolver: yupResolver(__formSchema),
    shouldFocusError: true,
    defaultValues: {
      fundraiseName: '',
      fundraiseDescription: '',
      targetCloseDate: '',
      targetAmountDollars: 0,
      fundingStage: 'preSeed'
    }
  });

  const campaign = tuple.data?.campaign;

  const defaultValues = React.useMemo(() => ({
    fundraiseName: campaign?.name,
    fundraiseDescription: campaign?.description,
    targetCloseDate: moment(campaign?.targetCloseDate).format('YYYY-MM-DD'),
    targetAmountDollars: campaign?.targetAmountDollars,
    fundingStage: campaign?.fundingStage.name
  }), [campaign]);



  React.useEffect(() => {
    form.reset(defaultValues);

  }, [
    form,
    campaign,
    defaultValues
  ]);



  return {
    form,
    loading,
    mutations: { saveCampaignMutation }
  };
}



function __useSaveCampaignMutation(id?: string) {
  const [fnMutate, tuple] = useSaveCampaignMutation({
    awaitRefetchQueries: _.isNil(id),
    refetchQueries: _.isNil(id) ? [refetchQueries.MyFundraisesActivity({})] : []
  });

  async function fnWrappedMutate(formData: IFormData) {
    await fnMutate({
      variables: {
        input: _.merge(
          {
            name: formData.fundraiseName,
            description: formData.fundraiseDescription,
            targetCloseDate: moment(formData.targetCloseDate).toISOString(),
            targetAmountDollars: formData.targetAmountDollars,
            fundingStage: formData.fundingStage
          },
          id ? { id } : {})
      }
    });
  }

  return [
    fnWrappedMutate,
    {
      ...tuple,
      data: null
    }
  ] as const;
}
